import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Dropdown,
  Menu,
  Popover,
  Badge,
  Form,
} from "antd";
import {
  MoreOutlined,
  SearchOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getPurchaseOrders, deletePurchaseOrder } from "../../../redux/actions/purchase/purchaseOrderAction";
import { loadAllSuppliersForSearch } from "../../../redux/actions/supplier/getSuppliersAction";

const { RangePicker } = DatePicker;
const { Option } = Select;

const PurchaseOrderList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, pagination, loading } = useSelector((state) => state.purchaseOrders);
  const allSuppliers = useSelector((state) => state.suppliers.searchList || state.suppliers.list) || [];

  const [filters, setFilters] = useState({
    status: "",
    supplier_id: "",
    start_date: "",
    end_date: "",
    page: 1,
    limit: 20,
  });
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterForm] = Form.useForm();
  const [tableScrollY, setTableScrollY] = useState(400);
  const tableWrapRef = useRef(null);

  useEffect(() => {
    if (!tableWrapRef.current) return;
    const el = tableWrapRef.current;
    const ro = new ResizeObserver(() => {
      setTableScrollY(Math.max(200, el.clientHeight - 88));
    });
    ro.observe(el);
    setTableScrollY(Math.max(200, el.clientHeight - 88));
    return () => ro.disconnect();
  }, [list]);

  // Sync form with current filters when opening the popover
  const handleFilterOpenChange = (open) => {
    setFilterVisible(open);
    if (open) {
      filterForm.setFieldsValue({
        status: filters.status || undefined,
        supplier_id: filters.supplier_id || undefined,
        dateRange: filters.start_date && filters.end_date
          ? [moment(filters.start_date), moment(filters.end_date)]
          : undefined,
      });
    }
  };

  useEffect(() => {
    dispatch(getPurchaseOrders(filters));
    dispatch(loadAllSuppliersForSearch({ status: true }));
  }, [dispatch, filters]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this purchase order?")) {
      const result = await dispatch(deletePurchaseOrder(id));
      if (result?.message === "success") {
        dispatch(getPurchaseOrders(filters));
      }
    }
  };

  const handleConvertToPurchase = (orderId) => {
    navigate(`/purchase?purchase_order_id=${orderId}`);
  };

  const handleConvertToSaleInvoice = (orderId) => {
    navigate(`/sale?purchase_order_id=${orderId}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "orange",
      partial: "blue",
      received: "green",
      cancelled: "red",
    };
    return colors[status] || "default";
  };

  const hasActiveFilter = filters.status || filters.supplier_id || filters.start_date || filters.end_date;

  const handleFilterApply = (values) => {
    const dateRange = values.dateRange;
    setFilters((prev) => ({
      ...prev,
      status: values.status || "",
      supplier_id: values.supplier_id || "",
      start_date: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : "",
      end_date: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : "",
      page: 1,
    }));
    setFilterVisible(false);
  };

  const handleFilterClear = () => {
    filterForm.resetFields();
    setFilters({
      status: "",
      supplier_id: "",
      start_date: "",
      end_date: "",
      page: 1,
      limit: filters.limit,
    });
    setFilterVisible(false);
  };

  const filterContent = (
    <div style={{ width: 280 }}>
      <Form
        form={filterForm}
        layout="vertical"
        onFinish={handleFilterApply}
      >
        <Form.Item name="status" label="Status">
          <Select placeholder="All statuses" allowClear>
            <Option value="pending">Pending</Option>
            <Option value="partial">Partial</Option>
            <Option value="received">Received</Option>
            <Option value="cancelled">Cancelled</Option>
          </Select>
        </Form.Item>
        <Form.Item name="supplier_id" label="Supplier">
          <Select
            placeholder="All suppliers"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.children ?? "").toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {allSuppliers.map((supplier) => (
              <Option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="dateRange" label="Date Range">
          <RangePicker format="DD-MM-YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Button onClick={handleFilterClear}>Clear</Button>
          <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
            Apply
          </Button>
        </Space>
      </Form>
    </div>
  );

  const columns = [
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
      render: (text, record) => (
        <a onClick={() => navigate(`/purchase-order/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: "Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      key: "supplier",
      render: (supplier) => supplier?.name || "-",
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Expected Delivery",
      dataIndex: "expected_delivery_date",
      key: "expected_delivery_date",
      render: (date) => date ? moment(date).format("DD-MM-YYYY") : "-",
    },
    {
      title: "Items",
      key: "items",
      render: (_, record) => record.order_items?.length || 0,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const menu = (
          <Menu>
          
            <Menu.Item
              key="view"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/purchase-order/${record.id}`)}
            >
              View Details
            </Menu.Item>
            {record.status !== "received" && record.status !== "cancelled" && (
              <>
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/purchase-order/edit/${record.id}`)}
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  key="purchase"
                  icon={<FileTextOutlined />}
                  onClick={() => handleConvertToPurchase(record.id)}
                >
                  Convert to Purchase
                </Menu.Item>
                <Menu.Item
                  key="invoice"
                  icon={<FileTextOutlined />}
                  onClick={() => handleConvertToSaleInvoice(record.id)}
                >
                  Convert to Invoice
                </Menu.Item>
              </>
            )}
            {record.status !== "received" && (
              <Menu.Item
                key="delete"
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDelete(record.id)}
              >
                Delete
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <span
              className="ant-dropdown-trigger"
              style={{
                cursor: "pointer",
                padding: "4px 8px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(0, 0, 0, 0.85)",
                fontSize: 18,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreOutlined />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="card column-design list-page-fill-viewport">
      <div className="card-body">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }} className="list-page-header">
          <h2 style={{ margin: 0 }}>Purchase Orders</h2>
          <Space wrap>
            <Popover
              content={filterContent}
              title="Filters"
              trigger="click"
              open={filterVisible}
              onOpenChange={handleFilterOpenChange}
              placement="bottomRight"
            >
              <Badge count={hasActiveFilter ? 1 : 0} size="small" offset={[-2, 2]}>
                <Button
                  icon={<FilterOutlined />}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </Badge>
            </Popover>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/purchase-order/add")}
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", fontWeight: 600 }}
            >
              Add Purchase Order
            </Button>
          </Space>
        </div>

        <div className="list-page-table-container">
          <div className="list-page-table-inner">
            <div ref={tableWrapRef} className="list-page-table-wrap">
              <Table
                columns={columns}
                dataSource={list || []}
                rowKey="id"
                loading={loading}
                scroll={{ x: true, y: tableScrollY }}
                pagination={{
                  current: filters.page,
                  total: pagination?.total || 0,
                  pageSize: filters.limit,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  onChange: (page, pageSize) => {
                    setFilters((prev) => ({
                      ...prev,
                      page,
                      limit: pageSize,
                    }));
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderList;
